import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';
import '../styles/Login.css';

function Login() {
    const navigate = useNavigate();
    const { signIn } = useAuth();
    const [clientId, setClientId] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Step 1: Verify the client ID exists in the practitioners table
            const trimmedId = clientId.trim();
            let foundPractitioner = null;

            // Allow admin login without client ID (empty ID field)
            // Admins don't need a practitioner ID
            if (trimmedId) {
                const { data: practitioners, error: practError } = await supabase
                    .from('practitioners')
                    .select('id, name');

                if (practError) throw practError;

                const matchingPractitioners = (practitioners || []).filter(p => 
                    p.id.toLowerCase().startsWith(trimmedId.toLowerCase())
                );

                foundPractitioner = matchingPractitioners.length === 1 ? matchingPractitioners[0] : null;

                if (!foundPractitioner) {
                    throw new Error('Identifiant client (IDE) invalide. Veuillez vérifier votre identifiant.');
                }
            }

            // Step 2: Authenticate with Supabase Auth
            const { data, error: authError } = await signIn(email, password);
            if (authError) throw authError;

            // Step 3: If a client ID was provided, verify the profile is linked to this practitioner
            if (foundPractitioner && data?.user) {
                const fullPractitionerId = foundPractitioner.id;
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('role, practitioner_id')
                    .eq('id', data.user.id)
                    .single();

                // If user is a client, verify practitioner_id matches
                if (profile?.role === 'client' && profile?.practitioner_id !== fullPractitionerId) {
                    // Update the link if not set yet (first login with IDE)
                    if (!profile.practitioner_id) {
                        await supabase
                            .from('profiles')
                            .update({ practitioner_id: fullPractitionerId })
                            .eq('id', data.user.id);
                    } else {
                        // Mismatch — sign out and reject
                        await supabase.auth.signOut();
                        throw new Error('Cet identifiant client ne correspond pas à votre compte.');
                    }
                }
            }

            // Navigation will be handled by AuthContext and RoleBasedRedirect
            navigate('/');
        } catch (err) {
            setError(err.message || 'Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>ENOVADENT</h1>
                    <p>Gestion de Laboratoire Dentaire</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label>Identifiant Client (IDE)</label>
                        <input
                            type="text"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder="Votre identifiant client"
                            autoComplete="off"
                            className="ide-input"
                        />
                        <small className="ide-hint">
                            Laissez vide si vous êtes administrateur
                        </small>
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="votre@email.com"
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <label>Mot de passe</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="••••••••"
                            minLength={6}
                            autoComplete="current-password"
                        />
                    </div>

                    <div className="forgot-password-link">
                        <Link to="/forgot-password">Mot de passe oublié ?</Link>
                    </div>

                    {error && (
                        <div className="message error">
                            {error}
                        </div>
                    )}

                    <button type="submit" className="submit-btn" disabled={loading}>
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                </form>

                <div className="login-footer">
                    <p>
                        Pas encore de compte ?
                        <Link to="/register">S'inscrire</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Login;
